import { motion } from "framer-motion";

function Intro() {
  return (
    <section className="relative bg-white py-20 lg:py-32 overflow-hidden w-screen">
      <div className="container mx-auto max-w-6xl px-6">
        {/* Opening Statement */}
        <motion.div 
          className="mb-20"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.p 
            style={{ 
              fontFamily: 'Inter, sans-serif', 
              fontSize: 'clamp(1.25rem, 2.5vw, 2rem)', 
              color: '#222',
              lineHeight: '1.7',
              maxWidth: '52rem'
            }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Some may find this a strange concept, the <span className="relative inline-block">
              <span className="relative z-10" style={{ fontWeight: '600' }}>number 7</span>
              <motion.span
                className="absolute bottom-0 left-0 right-0 h-2"
                style={{ backgroundColor: '#E63946', opacity: 0.3 }}
                initial={{ scaleX: 0, transformOrigin: 'left' }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              />
            </span> has drawn me closer to God in ways I never expected.
          </motion.p>
        </motion.div>

        {/* Question & Introduction */}
        <motion.div 
          className="mb-20"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <motion.p 
            className="mb-12"
            style={{ 
              fontFamily: 'Inter, sans-serif', 
              fontSize: 'clamp(1.125rem, 2vw, 1.5rem)', 
              color: '#555',
              lineHeight: '1.6',
              fontStyle: 'italic',
              maxWidth: '48rem'
            }}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            You might be wondering — how can a number speak to someone so deeply that it changes their life?
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <motion.div 
              className="mb-6"
              initial={{ scaleX: 0, transformOrigin: 'left' }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <div 
                className="w-24 h-1"
                style={{ backgroundColor: '#00E5FF' }}
              />
            </motion.div>
            
            <p style={{ 
              fontFamily: 'Inter, sans-serif', 
              fontSize: 'clamp(1.125rem, 2vw, 1.5rem)', 
              color: '#222',
              lineHeight: '1.6',
              maxWidth: '48rem'
            }}>
              Well, I'm <strong style={{ color: '#0A0A0A' }}>Jordan</strong>, and this is my story — a story of how a simple number we learn as children has come to define my walk with God as an adult in my thirties.
            </p>
          </motion.div>
        </motion.div>

        {/* Biblical Significance - Large Statement */}
        <motion.div 
          className="mb-20 relative"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Giant 7 Background */}
          <motion.div 
            className="absolute -top-32 -right-12 lg:-right-24 opacity-[0.03] select-none pointer-events-none"
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 0.03 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2 }}
          >
            <span style={{ 
              fontFamily: 'League Spartan, sans-serif', 
              fontSize: 'clamp(20rem, 40vw, 35rem)', 
              fontWeight: '900', 
              color: '#0A0A0A',
              lineHeight: '1'
            }}>
              7
            </span>
          </motion.div>

          <motion.p 
            className="mb-8"
            style={{ 
              fontFamily: 'Inter, sans-serif', 
              fontSize: 'clamp(1.125rem, 2vw, 1.5rem)', 
              color: '#444',
              lineHeight: '1.7',
              maxWidth: '52rem'
            }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            In the Christian faith, the number 7 is one of the most profoundly symbolic and recurring numbers in the Bible.
          </motion.p>

          <motion.h2 
            className="mb-8"
            style={{ 
              fontFamily: 'League Spartan, sans-serif', 
              fontSize: 'clamp(2.5rem, 6vw, 5rem)', 
              fontWeight: '900', 
              color: '#0A0A0A',
              letterSpacing: '-0.02em',
              lineHeight: '1',
              textTransform: 'uppercase',
              maxWidth: '56rem'
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Divine perfection,<br />
            completion,<br />
            <span className="relative inline-block">
              <span className="relative z-10">and covenant</span>
              <motion.span
                className="absolute bottom-0 left-0 right-0 h-4 lg:h-6"
                style={{ backgroundColor: '#E63946', opacity: 0.3 }}
                initial={{ scaleX: 0, transformOrigin: 'left' }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.5 }}
              />
            </span>
          </motion.h2>

          <motion.p 
            style={{ 
              fontFamily: 'Inter, sans-serif', 
              fontSize: 'clamp(1.125rem, 2vw, 1.5rem)', 
              color: '#444',
              lineHeight: '1.7',
              maxWidth: '52rem'
            }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            — a reflection of God's fullness and faithfulness.
          </motion.p>
        </motion.div>

        {/* Statistics */}
        <motion.div 
          className="mb-10 flex items-center gap-6 flex-wrap"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2, type: 'spring' }}
          >
            <span style={{ 
              fontFamily: 'League Spartan, sans-serif', 
              fontSize: 'clamp(4rem, 8vw, 7rem)', 
              fontWeight: '900', 
              color: '#E63946',
              lineHeight: '1',
              letterSpacing: '-0.02em'
            }}>
              735
            </span>
          </motion.div>
          <motion.p 
            style={{ 
              fontFamily: 'Inter, sans-serif', 
              fontSize: 'clamp(1rem, 1.5vw, 1.25rem)', 
              color: '#444',
              lineHeight: '1.6',
              maxWidth: '32rem'
            }}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            From Genesis to Revelation, the number 7 appears around <strong>735 times</strong>, each one a reminder of God's perfect design and promise.
          </motion.p>
        </motion.div>

        {/* Scripture Quotes */}
        <div className="space-y-10 mb-32">
          {/* Genesis Quote */}
          <motion.div
            className="relative pl-0 lg:pl-12"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.div 
              className="absolute left-0 top-0 bottom-0 w-1 hidden lg:block"
              style={{ backgroundColor: '#00E5FF' }}
              initial={{ scaleY: 0, transformOrigin: 'top' }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            />
            <p style={{ 
              fontFamily: 'Inter, sans-serif', 
              fontSize: 'clamp(1.125rem, 2vw, 1.5rem)', 
              color: '#222',
              lineHeight: '1.7',
              fontStyle: 'italic',
              maxWidth: '48rem'
            }}>
              "By the seventh day God had finished the work he had been doing; so on the seventh day he rested."
            </p>
            <p 
              className="mt-4"
              style={{ 
                fontFamily: 'League Spartan, sans-serif', 
                fontSize: '0.875rem', 
                color: '#00E5FF',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontWeight: '700'
              }}
            >
              — Genesis 2:2
            </p>
          </motion.div>

          {/* Revelation Quote */}
          <motion.div
            className="relative pl-0 lg:pl-12"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.div 
              className="absolute left-0 top-0 bottom-0 w-1 hidden lg:block"
              style={{ backgroundColor: '#E63946' }}
              initial={{ scaleY: 0, transformOrigin: 'top' }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            />
            <p style={{ 
              fontFamily: 'Inter, sans-serif', 
              fontSize: 'clamp(1.125rem, 2vw, 1.5rem)', 
              color: '#222',
              lineHeight: '1.7',
              fontStyle: 'italic',
              maxWidth: '48rem'
            }}>
              "Then I saw a Lamb, looking as if it had been slain, standing at the center of the throne… The Lamb had seven horns and seven eyes, which are the seven spirits of God sent out into all the earth."
            </p>
            <p 
              className="mt-4"
              style={{ 
                fontFamily: 'League Spartan, sans-serif', 
                fontSize: '0.875rem', 
                color: '#E63946',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontWeight: '700'
              }}
            >
              — Revelation 5:6
            </p>
          </motion.div>
        </div>

        {/* Closing Reflection */}
        <motion.div
          className="space-y-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div>
              <p style={{ 
                fontFamily: 'Inter, sans-serif', 
                fontSize: 'clamp(1.125rem, 2vw, 1.5rem)', 
                color: '#999',
                lineHeight: '1.7'
              }}>
                To some, it's just a number.
              </p>
            </div>
            <div>
              <p style={{ 
                fontFamily: 'Inter, sans-serif', 
                fontSize: 'clamp(1.125rem, 2vw, 1.5rem)', 
                color: '#222',
                lineHeight: '1.7',
                fontWeight: '500'
              }}>
                To me, it's been a <span className="relative inline-block">
                  <span className="relative z-10" style={{ fontWeight: '700', color: '#0A0A0A' }}>message</span>
                  <motion.span
                    className="absolute bottom-0 left-0 right-0 h-2"
                    style={{ backgroundColor: '#00E5FF', opacity: 0.4 }}
                    initial={{ scaleX: 0, transformOrigin: 'left' }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                  />
                </span> — a divine reminder that God completes what He begins.
              </p>
            </div>
          </motion.div>

          <motion.div
            className="pt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p style={{ 
              fontFamily: 'League Spartan, sans-serif', 
              fontSize: 'clamp(2rem, 4vw, 3.5rem)', 
              fontWeight: '900', 
              color: '#0A0A0A',
              letterSpacing: '-0.01em',
              lineHeight: '1.1',
              textTransform: 'uppercase'
            }}>
              And that's what<br />
              <span className="relative inline-block">
                <span className="relative z-10">Se7ens</span>
                <motion.span
                  className="absolute bottom-0 left-0 right-0 h-3 lg:h-4"
                  style={{ backgroundColor: '#E63946', opacity: 0.3 }}
                  initial={{ scaleX: 0, transformOrigin: 'left' }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                />
              </span> is all about.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default Intro;